import { Badge, Subheading, SurfaceCard, SurfaceRow } from '../../../components/ui'
import { LONGEVITY_DATA, HEALTHSPAN_CATEGORIES, BIOAGE_HISTORY, OPTIMAL_ZONES } from '../constants'
import { uiClasses } from '../../shared/uiClasses'
import { GradientSphereMetricCard, sphereColorAt } from '../components/GradientSphereCard'
import HealthAgeOrb from '../../landing/HealthAgeOrb'
import HealthAgingPaceCard from '../HealthAgingPaceCard'

const TEXT_PRIMARY = uiClasses.textPrimary
const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_SUBTLE = uiClasses.textSubtle
const TEXT_FAINT = uiClasses.textFaint
const BAR_TRACK = uiClasses.barTrack
const BAR_FILL_NEUTRAL = uiClasses.barFillNeutral
const TRACK_MARKER = uiClasses.trackMarker

const STATUS_BAR_COLOR: Record<string, string> = {
  optimal: 'dash-viz-score-high',
  good: 'dash-viz-score-mid',
  fair: 'dash-viz-score-low',
}

function progressPercent(score: number, max = 100): number {
  return Math.min(100, Math.max(0, (score / max) * 100))
}

export default function LongevityTab() {
  const {
    healthspanScore,
    rateOfAging,
    lastAssessment,
  } = LONGEVITY_DATA

  const maxChronoAge = Math.max(...BIOAGE_HISTORY.map((e) => e.chronAge))

  return (
    <div className="space-y-6">
      <section className="grid gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] xl:items-center">
        <SurfaceCard as="article" className="text-center">
          <div className="flex items-center justify-center">
            <HealthAgeOrb size="small" />
          </div>
        </SurfaceCard>

        <div className="w-full">
          <HealthAgingPaceCard />
        </div>
      </section>

      {/* ── Stats row ────────────────────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-3">
        <GradientSphereMetricCard
          label="Healthspan Score"
          sphereColor={sphereColorAt(0)}
          valueSize="lg"
          labelVariant="section"
          value={
            <>
              {healthspanScore}
              <span className={`gradient-sphere-metric-card__unit ${TEXT_FAINT}`}>/100</span>
            </>
          }
        />
        <GradientSphereMetricCard
          label="Rate of Aging"
          sphereColor={sphereColorAt(1)}
          valueSize="lg"
          labelVariant="section"
          value={
            <>
              {rateOfAging}
              <span className={`gradient-sphere-metric-card__unit ${TEXT_FAINT}`}>x</span>
            </>
          }
        />
        <GradientSphereMetricCard
          label="Last Assessment"
          value={lastAssessment}
          sphereColor={sphereColorAt(2)}
          valueSize="lg"
          labelVariant="section"
        />
      </div>

      {/* ── Healthspan categories grid ───────────────────────── */}
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Healthspan Categories</Subheading>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {HEALTHSPAN_CATEGORIES.map((cat) => (
            <SurfaceRow key={cat.id}>
              <div className="flex items-center justify-between">
                <p className={`text-sm font-medium ${TEXT_PRIMARY}`}>{cat.label}</p>
                <Badge variant="zinc">
                  {cat.status}
                </Badge>
              </div>
              <p className={`mt-2 text-xl font-semibold ${TEXT_PRIMARY}`}>
                {cat.score}<span className={`text-sm font-normal ${TEXT_FAINT}`}>/100</span>
              </p>
              <div className={`mt-2 h-2 w-full overflow-hidden rounded-full ${BAR_TRACK}`}>
                <div
                  className={`h-full rounded-full ${STATUS_BAR_COLOR[cat.status] || BAR_FILL_NEUTRAL}`}
                  style={{ width: `${progressPercent(cat.score)}%` }}
                />
              </div>
            </SurfaceRow>
          ))}
        </div>
      </SurfaceCard>

      {/* ── Bio age trend bar chart ──────────────────────────── */}
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Biological Age Trend</Subheading>
        <div className="mt-6 flex h-56 items-end gap-3">
          {BIOAGE_HISTORY.map((entry) => {
            const bioHeight = (entry.bioAge / maxChronoAge) * 100
            const chronoHeight = (entry.chronAge / maxChronoAge) * 100
            return (
              <div key={entry.date} className="flex flex-1 flex-col items-center gap-2">
                <div className="flex h-44 w-full items-end justify-center gap-1">
                  {/* Bio age bar (lighter) */}
                  <div
                    className="w-2/5 rounded-t-lg dash-viz-tone-2"
                    style={{ height: `${bioHeight}%` }}
                    title={`Bio Age: ${entry.bioAge}`}
                  />
                  {/* Chrono age bar (darker) */}
                  <div
                    className={`w-2/5 rounded-t-lg ${BAR_FILL_NEUTRAL}`}
                    style={{ height: `${chronoHeight}%` }}
                    title={`Chrono Age: ${entry.chronAge}`}
                  />
                </div>
                <span className={`text-xs/5 ${TEXT_SUBTLE}`}>{entry.date}</span>
              </div>
            )
          })}
        </div>
        <div className={`mt-4 flex items-center justify-center gap-6 text-xs ${TEXT_SUBTLE}`}>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm dash-viz-tone-2" /> Biological Age
          </span>
          <span className="flex items-center gap-1.5">
            <span className={`inline-block h-2.5 w-2.5 rounded-sm ${BAR_FILL_NEUTRAL}`} /> Chronological Age
          </span>
        </div>
      </SurfaceCard>

      {/* ── Optimal zones table ──────────────────────────────── */}
      <SurfaceCard as="article">
        <Subheading variant="dashboard">Optimal Zones</Subheading>
        <div className="mt-4 space-y-4">
          {OPTIMAL_ZONES.map((zone) => {
            const rangeMin = zone.normal[0]
            const rangeMax = zone.normal[1]
            const span = rangeMax - rangeMin
            const optimalLeft = ((zone.optimal[0] - rangeMin) / span) * 100
            const optimalWidth = ((zone.optimal[1] - zone.optimal[0]) / span) * 100
            const currentPos = Math.min(100, Math.max(0, ((zone.current - rangeMin) / span) * 100))

            return (
              <SurfaceRow key={zone.marker}>
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-medium ${TEXT_PRIMARY}`}>{zone.marker}</p>
                  <Badge variant="zinc">
                    {zone.status}
                  </Badge>
                </div>
                <p className={`mt-1 text-xs ${TEXT_SUBTLE}`}>
                  Current: <span className={`font-medium ${TEXT_SECONDARY}`}>{zone.current} {zone.unit}</span>
                  {' · '}Optimal: {zone.optimal[0]}–{zone.optimal[1]} {zone.unit}
                </p>
                {/* Range indicator */}
                <div className={`relative mt-2 h-3 w-full rounded-full ${BAR_TRACK}`}>
                  {/* Optimal zone highlight */}
                  <div
                    className="absolute inset-y-0 rounded-full dash-viz-zone"
                    style={{ left: `${optimalLeft}%`, width: `${optimalWidth}%` }}
                  />
                  {/* Current value marker */}
                  <div
                    className={`absolute top-1/2 h-4 w-1.5 -translate-y-1/2 rounded-full ${TRACK_MARKER}`}
                    style={{ left: `calc(${currentPos}% - 3px)` }}
                  />
                </div>
                <div className={`mt-1 flex justify-between text-[10px] ${TEXT_FAINT}`}>
                  <span>{rangeMin} {zone.unit}</span>
                  <span>{rangeMax} {zone.unit}</span>
                </div>
              </SurfaceRow>
            )
          })}
        </div>
      </SurfaceCard>
    </div>
  )
}
